import React, { createContext, useContext, useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Site context for managing multi-site configuration
const SiteContext = createContext(null);

// Default site configurations (fallback if API fails)
const DEFAULT_SITES = {
  cr: {
    site_id: 'cr',
    name: 'Cultural Revolution',
    tagline: 'Creator-led projects in film, media, and culture',
    theme: {
      mode: 'dark',
      primary_color: '#C41E3A',
      accent_color: '#C41E3A',
      font_package: 'editorial'
    },
    features: {
      directories: true,
      applications: false
    }
  },
  btt: {
    site_id: 'btt',
    name: 'Be The Train',
    tagline: 'Creator Accelerator for Filmmakers',
    theme: {
      mode: 'dark',
      primary_color: '#00A86B',
      accent_color: '#00A86B',
      font_package: 'cinematic',
      logo: 'https://customer-assets.emergentagent.com/job_5fff2c07-dfff-4220-a200-8d4fd9f6d613/artifacts/i6xi2h68_BTTLogo.png'
    },
    features: {
      directories: false,
      applications: true
    }
  }
};

/**
 * Detect which site we're on based on hostname/subdomain
 */
const detectSiteId = () => {
  const hostname = window.location.hostname.toLowerCase();
  
  // Check for BTT production domains
  if (hostname.includes('bethetrain.com')) {
    return 'btt';
  }
  
  // Check for CR production domains
  if (hostname.includes('culturalrevolution')) {
    return 'cr';
  }
  
  // Check for subdomain pattern (e.g., btt.preview.emergentagent.com or btt-members.preview.emergentagent.com)
  // This handles development/preview environments
  if (hostname.startsWith('btt.') || hostname.startsWith('btt-')) {
    return 'btt';
  }
  if (hostname.startsWith('cr.') || hostname.startsWith('cr-')) {
    return 'cr';
  }
  
  // Development: check query param as fallback
  const params = new URLSearchParams(window.location.search);
  const siteParam = params.get('site');
  if (siteParam === 'btt' || siteParam === 'cr') {
    return siteParam;
  }
  
  // Default to BTT for development/unknown domains
  return 'btt';
};

export const SiteProvider = ({ children }) => {
  const [siteConfig, setSiteConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Detected site ID
  const detectedSiteId = detectSiteId();
  
  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        // Fetch site config from API
        const res = await fetch(`${API_URL}/api/site-config?site=${detectedSiteId}`);
        
        if (res.ok) {
          const config = await res.json();
          setSiteConfig(config);
          
          // Apply theme CSS variables to document
          applyTheme(config.theme);
        } else {
          // Use default config
          const defaultConfig = DEFAULT_SITES[detectedSiteId] || DEFAULT_SITES.cr;
          setSiteConfig(defaultConfig);
          applyTheme(defaultConfig.theme);
        }
      } catch (err) {
        console.error('Failed to fetch site config:', err);
        setError(err.message);
        
        // Use default config on error
        const defaultConfig = DEFAULT_SITES[detectedSiteId] || DEFAULT_SITES.cr;
        setSiteConfig(defaultConfig);
        applyTheme(defaultConfig.theme);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSiteConfig();
  }, [detectedSiteId]);
  
  /**
   * Apply theme CSS variables to document root
   */
  const applyTheme = (theme) => {
    if (!theme) return;
    
    const root = document.documentElement;
    
    // Set CSS variables
    if (theme.primary_color) {
      root.style.setProperty('--site-primary', theme.primary_color);
    }
    if (theme.accent_color) {
      root.style.setProperty('--site-accent', theme.accent_color);
    }
    
    // Set theme mode class on body
    if (theme.mode === 'dark') {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }
  };
  
  // Computed values
  const isBTT = siteConfig?.site_id === 'btt';
  const isCR = siteConfig?.site_id === 'cr';
  
  // Helper to get themed color
  const getPrimaryColor = () => siteConfig?.theme?.primary_color || '#C41E3A';
  const getAccentColor = () => siteConfig?.theme?.accent_color || getPrimaryColor();
  
  // Helper to check feature flags
  const hasFeature = (featureName) => {
    return siteConfig?.features?.[featureName] ?? false;
  };
  
  const value = {
    // Core state
    siteConfig,
    loading,
    error,
    
    // Site identification
    siteId: siteConfig?.site_id || detectedSiteId,
    isBTT,
    isCR,
    
    // Theme helpers
    getPrimaryColor,
    getAccentColor,
    theme: siteConfig?.theme || {},
    
    // Navigation
    navConfig: siteConfig?.nav_config || {},
    footerConfig: siteConfig?.footer_config || {},
    
    // Features
    hasFeature,
    features: siteConfig?.features || {},
    
    // SEO
    seoDefaults: siteConfig?.seo_defaults || {},
    
    // Site info
    siteName: siteConfig?.name || 'Cultural Revolution',
    siteTagline: siteConfig?.tagline || '',
  };
  
  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

/**
 * Hook to access site context
 */
export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

/**
 * HOC for site-aware components
 */
export const withSite = (Component) => {
  return function SiteAwareComponent(props) {
    const siteContext = useSite();
    return <Component {...props} site={siteContext} />;
  };
};

export default SiteContext;
